import { Badge } from '#components/ui/badge'
import { Button } from '#components/ui/button'
import { Card } from '#components/ui/card'
import { createDate } from '#lib/date-service'

interface Child {
  id: string
  firstName: string
  lastName: string
  status:
    'active' | 'suspended' | 'cancelled' | 'expired' | 'pending' | 'past_due' | 'inactive' | 'ended'
  endDate?: string | null
  nextBilling?: string | null
  isSubscribed?: boolean
  hasParent: boolean
  commitment: boolean
  commitmentEndDate: string | null
}

interface SubscriptionUserCardProps {
  child: Child
  onCancel: () => void
  onRePay: () => void
  onRenew: () => void
  isActionLoading: boolean
}

export const SubscriptionUserCard: React.FC<SubscriptionUserCardProps> = ({
  child,
  onCancel,
  onRePay,
  onRenew,
  isActionLoading,
}) => {
  const getStatusBadge = () => {
    switch (child.status) {
      case 'active':
        return <Badge className="bg-green-500 text-white hover:bg-green-600">Actif</Badge>
      case 'suspended':
        return <Badge className="bg-yellow-500 text-white hover:bg-yellow-600">Suspendu</Badge>
      case 'cancelled':
        return (
          <Badge className="bg-orange-500 text-white hover:bg-orange-600">
            Annulation en cours
          </Badge>
        )
      default:
        return (
          <Badge variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-200">
            Pas d&#39;abonnement
          </Badge>
        )
    }
  }

  const getSecondaryText = () => {
    const formattedNextBilling = child.nextBilling
      ? createDate(child.nextBilling).format('DD/MM/YYYY')
      : 'N/A'
    const formattedEndDate = child.endDate ? createDate(child.endDate).format('DD/MM/YYYY') : 'N/A'
    const formattedCommitmentEndDate = child.commitmentEndDate
      ? createDate(child.commitmentEndDate).format('DD/MM/YYYY')
      : 'N/A'

    switch (child.status) {
      case 'active':
        if (child.commitment) {
          return (
            <p className="text-sm text-gray-500">
              Fin d&#39;engagement le {formattedCommitmentEndDate}
            </p>
          )
        }
        return (
          <p className="text-sm text-gray-500">Prochaine facturation le {formattedNextBilling}</p>
        )
      case 'suspended':
        return <p className="text-sm text-gray-500">L&#39;abonnement est actuellement suspendu</p>
      case 'cancelled':
        if (child.commitment) {
          return (
            <p className="text-sm text-gray-500">
              Fin d&#39;engagement le {formattedCommitmentEndDate}
            </p>
          )
        }
        return (
          <p className="text-sm text-gray-500">Fin de l&#39;abonnement le {formattedEndDate}</p>
        )
      case 'ended':
        return <p className="text-sm text-gray-500">Abonnement terminé le {formattedEndDate}</p>
      default:
        return null
    }
  }

  const getButton = () => {
    switch (child.status) {
      case 'active':
        return (
          <div className="flex flex-col gap-4">
            <Button
              variant="destructive"
              className="w-full border-none"
              onClick={onCancel}
              disabled={isActionLoading || child.hasParent}
            >
              Annuler
            </Button>
          </div>
        )
      case 'suspended':
        return (
          <div className="flex flex-col gap-4">
            <Button
              variant="warning"
              className="w-full border-none"
              onClick={onRePay}
              disabled={isActionLoading}
            >
              Payer
            </Button>
          </div>
        )
      case 'cancelled':
        return (
          <div className="flex flex-col gap-4">
            <Button
              variant="destructive"
              className="w-full border-none"
              onClick={onRenew}
              disabled={isActionLoading || child.hasParent}
            >
              Réactiver
            </Button>
          </div>
        )
      default:
        return <div className="hidden h-0 w-0"></div>
    }
  }

  return (
    <Card key={child.id} className="h-full p-6">
      <div className="flex h-full flex-col justify-between gap-6">
        <div className="flex justify-between">
          <div className="flex gap-x-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-blue-600">
              {child.firstName[0]}
            </div>
            <div className="flex flex-col">
              <h3 className="text-lg font-bold text-gray-900">
                {child.firstName} {child.lastName}
              </h3>
              <div>{getSecondaryText()}</div>
            </div>
          </div>

          <div>{getStatusBadge()}</div>
        </div>
        <div>{getButton()}</div>
      </div>
    </Card>
  )
}
